import { Link } from "react-router-dom";
import { formatDate, getCategoryById, type Article } from "@/utils/data";

interface ArticleCardProps {
  article: Article;
  variant?: "vertical" | "horizontal";
  rank?: number;
}

export default function ArticleCard({ article, variant = "vertical", rank }: ArticleCardProps) {
  const category = getCategoryById(article.categoryId);

  if (variant === "horizontal") {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex gap-4 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
      >
        {typeof rank === "number" && (
          <span className="shrink-0 w-6 text-center font-heading text-lg font-bold text-accent-500">
            {rank}
          </span>
        )}
        <div className="shrink-0 w-28 h-20 sm:w-32 sm:h-[88px] rounded-md overflow-hidden bg-background-100">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} 自慰健康生活`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="min-w-0 flex flex-col justify-between py-0.5">
          <div>
            <div className="flex items-center gap-2 mb-1 text-xs text-foreground-500">
              {category && <span className="text-primary-600">{category.name}</span>}
              <span>· {formatDate(article.date)}</span>
            </div>
            <h3 className="text-sm md:text-base font-medium text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-2">
              {article.title}
            </h3>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-xs text-foreground-400 mt-2">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.readCount.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
    >
      <div className="relative w-full h-44 md:h-48 overflow-hidden bg-background-100">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} 自慰健康生活`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {category && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs bg-background-50/90 text-primary-700">
            {category.name}
          </span>
        )}
      </div>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <h3 className="text-base font-medium text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">{article.summary}</p>
        <div className="mt-4 flex items-center justify-between text-xs text-foreground-400">
          <span>{formatDate(article.date)}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.readCount.toLocaleString()}
          </span>
        </div>
      </div>
    </Link>
  );
}